import { useEffect, useState } from 'react';

/**
 * useCanI — ask the BFF whether the logged-in operator is permitted
 * to perform `task` on `item`.
 *
 * Usage:
 *   const { loading, can, reason } = useCanI('delete_station', { depth: 'station', id: 1234 });
 *   if (loading) return null;
 *   if (!can) return null;  // or: <button disabled title={reason}>Delete</button>
 *   return <button onClick={...}>Delete</button>;
 *
 * Returns:
 *   { loading: bool, can: bool, reason: string | null }
 *
 * Caching: per-page-load, keyed by `${task}:${depth}:${id}`. Mounts that
 * ask the same question share the response. Cache lives until the page
 * is reloaded; logout/login is a page reload in practice. This is
 * deliberately simple — invalidation on grant/revoke would need a
 * websocket or polling, neither warranted in v1. If the operator just
 * had a permission granted, they refresh; until then they see the
 * pre-grant state (and the BFF still gates correctly regardless of UI).
 *
 * `item` may be null/undefined for routes that haven't picked an item
 * yet (e.g. before a station is selected). In that case loading stays
 * true and can stays false; consumers just render the "not yet"
 * placeholder.
 */

const _cache = new Map();
const _inflight = new Map();


export function useCanI(task, item) {
  const key = item ? `${task}:${item.depth}:${item.id}` : null;
  const [state, setState] = useState(() => {
    if (key && _cache.has(key)) return _cache.get(key);
    return { loading: true, can: false, reason: null };
  });

  useEffect(() => {
    if (!key) {
      setState({ loading: true, can: false, reason: null });
      return;
    }
    if (_cache.has(key)) {
      setState(_cache.get(key));
      return;
    }
    let cancelled = false;

    const fire = async () => {
      const url = `/api/permissions/check?task=${encodeURIComponent(task)}`
                + `&item_depth=${encodeURIComponent(item.depth)}`
                + `&item_id=${encodeURIComponent(item.id)}`;
      try {
        const r = await fetch(url, { credentials: 'same-origin' });
        if (r.status === 401) {
          // Session expired — let LoginGate handle it on the next op.
          return { loading: false, can: false, reason: 'session expired' };
        }
        if (!r.ok) {
          return { loading: false, can: false, reason: `HTTP ${r.status}` };
        }
        const j = await r.json();
        return { loading: false, can: !!j.can, reason: j.reason || null };
      } catch (e) {
        return { loading: false, can: false, reason: String(e.message || e) };
      }
    };

    // Single-flight: if another consumer is already asking the same
    // question, await their promise instead of firing a duplicate.
    let promise = _inflight.get(key);
    if (!promise) {
      promise = fire();
      _inflight.set(key, promise);
    }
    promise.then((next) => {
      _cache.set(key, next);
      _inflight.delete(key);
      if (!cancelled) setState(next);
    });

    return () => { cancelled = true; };
  }, [key, task, item ? item.depth : null, item ? item.id : null]);

  return state;
}


/**
 * Clear the per-page cache. Mostly useful right after a grant/revoke
 * the operator just performed (the UI knows the answer changed, even
 * though no server-push has fired).
 */
export function clearCanICache() {
  _cache.clear();
  _inflight.clear();
}
